// src/pages/ingresos/components/POTable.jsx
import React, { useRef, useState } from "react";
import { supabase } from "../../../utils/supabase";
import { scrollManager } from "../../../utils/scrollManager"; // ✅ Importar el gestor

// ✅ Batch print QR (un solo trabajo de impresión)
import {
  buildBatchQrPdfPages,
  printPdfBlobInHiddenIframe,
} from "../../../utils/qr-batch-print";

import PrintOSButton from "./PrintOSButton";
import InSituOSButton from "./InSituOSButton";
import ServiceOrderObservationsModal from "./ServiceOrderObservationsModal";
import CalibrationPlaceIcon from "./CalibrationPlaceIcon";
import { useLabPermissions } from "../../../hooks/useLabPermissions";
import { canSetStatus } from "../../../utils/statusPermissions";

const SERVICE_ORDER_STATUSES = [
  { value: "delivered_to_lab", label: "Entregado a laboratorio (ADM-Paola)" },
  { value: "calibration_finished", label: "Calibración finalizada (LAB-Técn. asignado)" },
  {
    value: "certificates_finished_controlled",
    label: "Certificados finalizados y controlados (LAB-Diego)",
  },
  { value: "uploaded_for_remito", label: "Subido para remitar (LAB-Diego)" },
  { value: "remito_generated", label: "Remito generado (ADM-Paola)" },
  { value: "client_notified", label: "Cliente avisado (ADM-Paola/Dali)" },
];

const LEGACY_STATUS_TO_NEW = {
  pending: "delivered_to_lab",
  in_progress: "calibration_finished",
  completed: "client_notified",
  blocked: "uploaded_for_remito",
};

const normalizeServiceOrderStatus = (status) => {
  const s = String(status || "").trim();
  if (!s) return "delivered_to_lab";
  if (SERVICE_ORDER_STATUSES.some((x) => x.value === s)) return s;
  return LEGACY_STATUS_TO_NEW[s] || "delivered_to_lab";
};

// Estados en dos grupos (como pidió gerencia): durante el proceso / de cierre.
// El ORDEN y las etiquetas replican EXACTAMENTE el Excel de la gerencia (para
// no confundir a los técnicos). Los estados internos que el Excel no lista
// ("Listo para remitar" automático y "Entregado" genérico) se ocultan del
// dropdown (ver HIDDEN_STATUS_OPTIONS) salvo que el instrumento ya esté en ellos.
const INSTRUMENT_STATUS_GROUPS = [
  {
    group: "Durante el proceso",
    // Orden según la matriz de la gerencia: ADM (recepción/depósito) →
    // Técnico+Jefe (proceso/calibrado/con-sin cert) → Solo Jefe
    // (pausado/rechazado/revisado) → ADM (remitido). "Listo para remitar"
    // es automático pero se deja en su lugar de flujo (tras revisado).
    options: [
      { value: "pending_reception",   label: "Pendiente de recepción" },
      { value: "active",              label: "En depósito" },
      { value: "in_progress",         label: "En proceso" },
      { value: "calibrated",          label: "Calibrado" },
      { value: "with_certificate",    label: "Con certificado" },
      { value: "without_certificate", label: "Sin certificado" },
      { value: "paused",              label: "Pausado" },
      { value: "rejected",            label: "Rechazado" },
      { value: "reviewed",            label: "Revisado por jefe" },
      { value: "ready_to_remit",      label: "Listo para remitar" },
      { value: "remitted",            label: "Remitido" },
    ],
  },
  {
    group: "De cierre",
    options: [
      { value: "delivered",                     label: "Entregado" },
      { value: "delivered_with_certificate",    label: "Entregado con certificado" },
      { value: "delivered_without_certificate", label: "Entregado sin certificado" },
      { value: "delivered_not_calibrated",      label: "Entregado no calibrado" },
      { value: "delivered_not_repaired",        label: "Entregado no reparado" },
      { value: "delivered_repaired",            label: "Entregado reparado" },
      { value: "cancelled",                     label: "Cancelado" },
    ],
  },
];

const INSTRUMENT_STATUS_OPTIONS = INSTRUMENT_STATUS_GROUPS.flatMap((g) => g.options);

const INSTRUMENT_STATUS_LABELS = INSTRUMENT_STATUS_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

// Estados que existen internamente pero NO están en el Excel de la gerencia:
// se ocultan del dropdown para no confundir. "Listo para remitar" es automático
// (post "Revisado") y "Entregado" es un estado genérico heredado. Se siguen
// mostrando si el instrumento YA está en ese estado (para que el <select> lo
// refleje bien). Sus etiquetas siguen disponibles para las insignias.
const HIDDEN_STATUS_OPTIONS = ["ready_to_remit", "delivered"];

export default function POTable({
  canAssignTechnicians = true,
  canRegisterLabEntry = true,
  canManageOperations = true,
  canSetOsStartSequence = true,
  canDeleteInstruments = true,
  canDeletePurchaseOrders = true,
  filteredPOs,
  poTechnicianName,
  poInstrumentCounts,
  openRows,
  scanningForPo,
  poInstruments,
  loading,
  scannerValue,
  setScannerValue,
  onToggleRow,
  onStartScanning,
  onStopScanning,
  onOpenAssign,
  onOpenRemitos,
  onScannerKeyDown,
  onOpenQRModal,
  onRefreshInstruments,
  onOpenOperations,
  onLinkIntake,
  unassignedByClient,
  onEditInstrument,
  onDeleteInstrument,
  onUpdateInstrumentStatus,
  onUpdateAllInstrumentStatuses,
  onAdminDeleteOS,
  onSetStartSeq,
  onLabEntry,
}) {
  // Solo el jefe de laboratorio puede marcar "Revisado por jefe" (ni admin ni técnico)
  const { role } = useLabPermissions();

  // 1.2) Helpers de scroll
  const lastScrollYRef = useRef(0);

  const getScrollYLockedAware = () => {
    // Si el scroll está bloqueado por scrollManager, body.top suele quedar "-123px"
    const top = document?.body?.style?.top;
    if (top && top.includes("px")) {
      const n = Math.abs(parseInt(top, 10));
      if (!Number.isNaN(n)) return n;
    }
    return window.scrollY || 0;
  };

  const restoreScroll = (y) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
      });
    });
  };

  // Estado local para el modal de observaciones
  const [obsModal, setObsModal] = useState({
    open: false,
    serviceOrderId: null,
  });
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [bulkStatusByPo, setBulkStatusByPo] = useState({});
  const [bulkSavingPoId, setBulkSavingPoId] = useState(null);

  // ✅ Estado para imprimir todos los QR de una OC en un solo trabajo
  const [printingPoId, setPrintingPoId] = useState(null);

  const handleBulkStatusUpdate = async (po) => {
    const nextStatus = bulkStatusByPo[po.id] || "";
    if (!nextStatus || !canSetStatus(role, nextStatus)) return;

    const rows = Array.from(
      new Map(
        (poInstruments?.[po.id] || [])
          .filter((row) => row?.instrument_id)
          .map((row) => [row.instrument_id, row])
      ).values()
    );
    const rowsToUpdate = rows.filter(
      (row) => (row.instrument_status || "active") !== nextStatus
    );

    if (!rowsToUpdate.length) {
      alert("Todos los instrumentos de esta orden ya tienen el estado seleccionado.");
      return;
    }

    const statusLabel = INSTRUMENT_STATUS_LABELS[nextStatus] || nextStatus;
    const orderLabel = po.code || po.po_number || po.id;
    const ok = window.confirm(
      `¿Cambiar ${rowsToUpdate.length} instrumento${
        rowsToUpdate.length === 1 ? "" : "s"
      } de la orden ${orderLabel} al estado "${statusLabel}"?`
    );
    if (!ok) return;

    try {
      setBulkSavingPoId(po.id);
      await Promise.resolve(
        onUpdateAllInstrumentStatuses?.(rowsToUpdate, po, nextStatus)
      );
      setBulkStatusByPo((current) => ({ ...current, [po.id]: "" }));
    } catch (e) {
      // El contenedor muestra el detalle mediante el aviso general de la pantalla.
      console.error("[POTable] bulk status update error", e);
    } finally {
      setBulkSavingPoId(null);
    }
  };

  // ✅ payload que codifica el QR (preferimos lab_id si existe)
  const getInstrumentPayloadFromRow = (r) => {
    const lab =
      r?.instrument_lab_id ??
      r?.instrument_labId ??
      r?.lab_id ??
      r?.labId ??
      r?.instrument_lab ??
      r?.lab ??
      null;

    if (lab !== null && lab !== undefined) {
      const s = String(lab).trim();
      if (s) return s;
    }

    const id = r?.instrument_id ?? r?.instrumentId ?? r?.id ?? null;
    if (id) return String(id);

    const tag = r?.instrument_tag ?? r?.tag ?? null;
    if (tag) return String(tag);

    return null;
  };

  // ✅ Obtener instrumentos de una OC (usa cache si ya está expandida)
  const fetchInstrumentsByPOForPrint = async (poId) => {
    const cached = poInstruments?.[poId];
    if (Array.isArray(cached) && cached.length) return cached;

    // 1) Intento principal: view enriquecida (si existe en tu proyecto)
    // Nota: seleccionamos campos típicos; si alguno no existe, igual Supabase puede fallar.
    // Por eso hacemos try/catch y luego fallback.
    try {
      const { data, error } = await supabase
        .from("v_service_orders_enriched")
        .select(
          "po_id, code, instrument_id, instrument_lab_id, instrument_tag, instrument_brand, instrument_model, instrument_serial"
        )
        .eq("po_id", poId)
        .order("code", { ascending: true });

      if (error) throw error;
      if (Array.isArray(data) && data.length) return data;
    } catch (e) {
      // seguimos con fallback
      console.warn("[POTable] fallback query instruments (view) failed:", e);
    }

    // 2) Fallback: service_orders + instruments (si tu schema lo permite)
    // service_orders: (po_id, instrument_id, code)
    // instruments: (id, lab_id, tag, brand, model, serial)
    const { data, error } = await supabase
      .from("service_orders")
      .select(
        "po_id, code, instrument_id, instruments:instrument_id (id, lab_id, tag, brand, model, serial)"
      )
      .eq("po_id", poId)
      .order("code", { ascending: true });

    if (error) throw error;

    // Normalizamos al mismo shape que el resto de la tabla
    return (data || []).map((r) => ({
      po_id: r.po_id,
      code: r.code,
      instrument_id: r.instrument_id,
      instrument_lab_id: r.instruments?.lab_id ?? null,
      instrument_tag: r.instruments?.tag ?? null,
      instrument_brand: r.instruments?.brand ?? null,
      instrument_model: r.instruments?.model ?? null,
      instrument_serial: r.instruments?.serial ?? null,
    }));
  };

  // ✅ Imprime todos los QR de una OC en UN SOLO JOB (PDF multi-página)
  // Si recibimos `preopenedWindow`, imprimimos ahí (mejor para evitar bloqueos).
  const printAllQrsForPO = async (po, preopenedWindow = null) => {
    try {
      setPrintingPoId(po.id);

      const rows = await fetchInstrumentsByPOForPrint(po.id);
      if (!rows?.length) {
        alert("Esta OC no tiene instrumentos para imprimir.");
        return;
      }

      const payloads = Array.from(
        new Set(
          rows
            .map((r) => getInstrumentPayloadFromRow(r))
            .map((v) => String(v || "").trim())
            .filter(Boolean)
        )
      );

      if (!payloads.length) {
        alert("No se encontraron instrumentos con ID/Tag para imprimir.");
        return;
      }

      // Reutilizamos la misma lógica estable del modo individual:
      // - etiqueta 20x18 mm
      // - QR con parámetros robustos para impresión
      const printedInstrumentIds = Array.from(
        new Set(
          rows
            .map((r) => r?.instrument_id ?? r?.instrumentId ?? null)
            .filter(Boolean)
        )
      );

      const { blob } = await buildBatchQrPdfPages({ payloads });

      // Preferir ventana pre-abierta (user gesture) para que no lo bloquee el navegador
      if (preopenedWindow && !preopenedWindow.closed) {
        const url = URL.createObjectURL(blob);
        preopenedWindow.document.write(`<!doctype html>
<html>
  <head><title>Imprimir QRs</title></head>
  <body style="margin:0">
    <iframe
      src="${url}"
      style="border:0;width:100%;height:100vh"
      onload="this.contentWindow.focus();this.contentWindow.print();"
    ></iframe>
  </body>
</html>`);
        preopenedWindow.document.close();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        // Fallback: iframe invisible en la misma ventana
        printPdfBlobInHiddenIframe(blob);
      }

      if (printedInstrumentIds.length) {
        const { data: authData } = await supabase.auth.getUser();
        const { error: printedError } = await supabase
          .from("instruments")
          .update({
            qr_printed_at: new Date().toISOString(),
            qr_printed_by: authData?.user?.id || null,
          })
          .in("id", printedInstrumentIds);

        if (printedError) {
          console.warn("[POTable] no se pudo registrar impresion QR:", printedError);
        }
      }
    } catch (e) {
      console.error(e);
      alert(e?.message || "No se pudo imprimir todos los QRs.");
    } finally {
      setPrintingPoId(null);
    }
  };

  // Formato de fecha DD/MM/AAAA
  const parseDateOnly = (dateStr) => {
    if (!dateStr) return null;
    const raw = String(dateStr).trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return { year, month, day };
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return {
      year: String(d.getFullYear()),
      month: String(d.getMonth() + 1).padStart(2, "0"),
      day: String(d.getDate()).padStart(2, "0"),
    };
  };

  const formatDateEU = (dateStr) => {
    const parts = parseDateOnly(dateStr);
    if (!parts) return "";
    return `${parts.day}/${parts.month}/${parts.year}`;
  };

  const formatDateShort = (dateStr) => {
    const parts = parseDateOnly(dateStr);
    if (!parts) return "";
    return `${parts.day}/${parts.month}`;
  };

  const Row = ({ po }) => {
    const isOpen = !!openRows[po.id];
    const isScanning = scanningForPo === po.id;
    const isPrintingThis = printingPoId === po.id;
    const orderDisplayCode = (po.code || `OS-${po.base_number || "—"}`).replace(
      /^OC-/i,
      "OS-"
    );

    const count = poInstrumentCounts?.[po.id] ?? 0;
    const tech = poTechnicianName?.[po.id] ?? "—";

    const pendingForClient = unassignedByClient?.[po.client_id] ?? 0;

    const priority = po.ops_priority || "medium";
    const status = normalizeServiceOrderStatus(po.ops_status);
    const dueDate = po.ops_due_date || null;

    const isOfferClosed = po.offer_status === "closed";
    const isOSCompleted = status === "client_notified";
    const isBlocked = isOfferClosed || isOSCompleted;

    const showPendingForClient =
      pendingForClient > 0 &&
      (
        status === "delivered_to_lab" ||
        status === "calibration_finished" ||
        status === "certificates_finished_controlled" ||
        status === "uploaded_for_remito"
      ) &&
      !isBlocked;

    const priorityConfig =
      {
        low: {
          label: "Baja",
          className:
            "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200",
          icon: "🟢",
        },
        medium: {
          label: "Media",
          className: "bg-sky-500/10 border border-sky-500/40 text-sky-200",
          icon: "🔵",
        },
        high: {
          label: "Alta",
          className:
            "bg-amber-500/10 border border-amber-500/40 text-amber-200",
          icon: "🟠",
        },
        urgent: {
          label: "Urgente",
          className: "bg-red-500/10 border border-red-500/40 text-red-200",
          icon: "🔥",
        },
      }[priority] ?? {
        label: priority,
        className: "bg-white/10 border border-white/30 text-white",
        icon: "•",
      };

    const statusConfig =
      {
        delivered_to_lab: {
          label: "Entregado a laboratorio (ADM-Paola)",
          className: "bg-slate-500/10 border border-slate-500/40 text-slate-200",
          icon: "📥",
        },
        calibration_finished: {
          label: "Calibración finalizada (LAB-Técn. asignado)",
          className: "bg-indigo-500/10 border border-indigo-500/40 text-indigo-200",
          icon: "🧪",
        },
        certificates_finished_controlled: {
          label: "Certificados finalizados y controlados (LAB-Diego)",
          className: "bg-violet-500/10 border border-violet-500/40 text-violet-200",
          icon: "📄",
        },
        uploaded_for_remito: {
          label: "Subido para remitar (LAB-Diego)",
          className: "bg-amber-500/10 border border-amber-500/40 text-amber-200",
          icon: "📤",
        },
        remito_generated: {
          label: "Remito generado (ADM-Paola)",
          className: "bg-cyan-500/10 border border-cyan-500/40 text-cyan-200",
          icon: "🧾",
        },
        client_notified: {
          label: "Cliente avisado (ADM-Paola/Dali)",
          className: "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200",
          icon: "📞",
        },
      }[status] ?? {
        label: status,
        className: "bg-white/10 border border-white/30 text-white",
        icon: "•",
      };

    const hasTech = tech && tech !== "—";
    const techChipClass = hasTech
      ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200"
      : "bg-slate-500/10 border border-slate-500/40 text-slate-300";

    const instrChipClass =
      "bg-violet-500/10 border border-violet-500/40 text-violet-200";
    const dueLabel = dueDate ? formatDateEU(dueDate) : null;

    // ✅ Función para abrir el modal - BLOQUEA EL SCROLL PRIMERO
    const openObservations = async () => {
      try {
        // 🔒 PASO 1: Bloquear scroll ANTES de cambiar el estado
        scrollManager.lock();

        // PASO 2: Obtener el service order ID
        const { data, error } = await supabase
          .from("service_orders")
          .select("id")
          .eq("po_id", po.id)
          .order("created_at", { ascending: true })
          .limit(1);

        if (error) throw error;

        if (!data || data.length === 0) {
          // Si no hay datos, desbloquear scroll y mostrar alerta
          scrollManager.unlock();
          alert("Esta OC todavía no tiene Órdenes de Servicio creadas.");
          return;
        }

        // PASO 3: Cambiar el estado para abrir el modal
        setObsModal({
          open: true,
          serviceOrderId: data[0].id,
        });
      } catch (e) {
        console.error(e);
        scrollManager.unlock(); // Desbloquear si hay error
        alert("No se pudo abrir Observaciones");
      }
    };

    // ✅ Mostrar “Tipo de instrumento” (categoría) en la tabla expandible
    const getInstrumentCategoryLabel = (r) => {
      const v =
        r?.instrument_category_name ??
        r?.instrument_category ??
        r?.category_name ??
        r?.tipo_instrumento ??
        r?.instrument_type_name ??
        r?.category ??
        null;
      const s = (v ?? "").toString().trim();
      return s || "—";
    };

    return (
      <div data-po-id={po.id} className="relative rounded-lg border border-white/10 mb-8">
        <div className="rounded-t-lg bg-white/5 px-3 py-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
          <div className="flex items-center gap-2">
            {/* 1.3) Arreglar el botón de desplegar */}
            <button
              type="button"
              className="px-2 py-1 rounded-md bg-white/10 hover:bg-white/15"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();

                const y = window.scrollY;
                await Promise.resolve(onToggleRow(po.id));
                restoreScroll(y);
              }}
            >
              {isOpen ? "▾" : "▸"}
            </button>
            <div className="flex flex-col">
              <div className="font-medium">
                {orderDisplayCode}
              </div>
              <div className="text-xs opacity-80">{po.client_name}</div>
            </div>
          </div>

          <div className="mt-1 sm:mt-0 flex flex-wrap items-center gap-2 sm:ml-auto">
            <span
              className={
                "px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 " +
                priorityConfig.className
              }
            >
              <span>{priorityConfig.icon}</span>
              <span>Prioridad: {priorityConfig.label}</span>
            </span>
            <span
              className={
                "px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 " +
                statusConfig.className
              }
            >
              <span>{statusConfig.icon}</span>
              <span>{statusConfig.label}</span>
            </span>
            {isBlocked && (
              <span className="px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 bg-red-600/20 border border-red-500/60 text-red-100">
                <span>⛔</span>
                <span>Ingreso bloqueado{isOfferClosed && " (oferta cerrada)"}</span>
              </span>
            )}
            {dueLabel && (
              <span className="px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 bg-slate-500/10 border border-slate-500/40 text-slate-200">
                <span>📅</span>
                <span>Vence: {dueLabel}</span>
              </span>
            )}
            <span
              className={
                "px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 " +
                techChipClass
              }
            >
              <span>👤</span>
              <span>{hasTech ? tech : "Sin técnico"}</span>
            </span>
            <span
              className={
                "px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 " +
                instrChipClass
              }
            >
              <span>🧪</span>
              <span>{count} instr.</span>
            </span>
            {showPendingForClient && (
              <span className="px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 bg-amber-500/10 border border-amber-500/40 text-amber-200">
                <span>⚠️</span>
                <span>Pendientes: {pendingForClient}</span>
              </span>
            )}
          </div>
        </div>

        <div
          className={`px-3 py-2 flex flex-wrap items-center gap-2 ${
            isOpen
              ? "sticky top-[101px] z-30 border-y border-white/10 bg-[#111722]/95 shadow-xl backdrop-blur"
              : "rounded-b-lg"
          }`}
          aria-label={`Acciones de ${orderDisplayCode}`}
        >
          {isOpen && (
            <span className="mr-1 whitespace-nowrap rounded-md border border-sky-400/30 bg-sky-500/10 px-2 py-1.5 text-xs font-semibold text-sky-100">
              {orderDisplayCode}
            </span>
          )}
          {!isBlocked && (
            <>
              <button
                type="button"
                className="px-2 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                onClick={() => onStartScanning(po.id)}
              >
                📱 Escanear
              </button>
              <a
                className="px-2 py-1.5 rounded-md border border-white/20 hover:bg-white/10"
                href={`/simple-instrument-form?poId=${po.id}`}
              >
                ➕ Ingresar
              </a>
            </>
          )}

          {canAssignTechnicians && (
            <button
              type="button"
              className="px-2 py-1.5 rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              onClick={() => onOpenAssign(po.id)}
            >
              👤 Asignar
            </button>
          )}

          {/* Bajar al laboratorio — solo antes de delivered_to_lab */}
          {canRegisterLabEntry && !["delivered_to_lab","calibration_finished","certificates_finished_controlled","uploaded_for_remito","remito_generated","client_notified"].includes(status) && (
            <button
              type="button"
              className="px-2 py-1.5 rounded-md border border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
              title="Registra la fecha de bajada al laboratorio en todos los instrumentos de esta OS"
              onClick={() => onLabEntry?.(po)}
            >
              📥 Bajar al lab
            </button>
          )}

          {canManageOperations && (
            <button
              type="button"
              className="px-2 py-1.5 rounded-md border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10"
              title="Definir prioridad, estado, vencimiento y notas operativas"
              onClick={() => onOpenOperations?.(po)}
            >
              🗂 Operaciones
            </button>
          )}

          <button
            type="button"
            className="px-2 py-1.5 rounded-md border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            onClick={() => onOpenRemitos(po)}
          >
            📄 Remitos
          </button>

          {!isBlocked && (
            <button
              type="button"
              className="px-2 py-1.5 rounded-md border border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
              title="Vincular instrumentos de un ingreso provisorio (por cliente) a esta OC"
              onClick={() => onLinkIntake?.(po)}
            >
              🔗 Vincular ingreso
            </button>
          )}

          <button
            type="button"
            className="px-2 py-1.5 rounded-md border border-slate-500/40 text-slate-200 hover:bg-slate-500/10"
            title="Observaciones administrativas que saldrán en el PDF"
            onClick={openObservations}
          >
            📝 Observaciones
          </button>

          <PrintOSButton po={po} />
          <InSituOSButton po={po} />


          {/* ✅ Cambio crítico: definir número inicial (os_start_seq) */}
          {canSetOsStartSequence && (
            <button
              type="button"
              className="px-2 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              title="Definir el número inicial (ej: 40 → 040, 041, 042...)"
              onClick={() => onSetStartSeq?.(po)}
            >
              # Inicio
            </button>
          )}

          {/* ✅ Cambio crítico: borrar OS/OC (solo si está vacía) */}
          {canDeletePurchaseOrders && count === 0 && (
            <button
              type="button"
              className="px-2 py-1.5 rounded-md border border-red-500/60 text-red-200 hover:bg-red-500/10"
              title="Borrar OS/OC (requiere contraseña crítica)"
              onClick={() => onAdminDeleteOS?.(po)}
            >
              🗑️ Borrar OS
            </button>
          )}


          {/* ✅ NUEVO: imprimir todos los QR de la OC en un solo trabajo */}
          <button
            type="button"
            className="px-2 py-1.5 rounded-md border border-white/20 hover:bg-white/10 disabled:opacity-60"
            disabled={isPrintingThis}
            title="Imprime todas las etiquetas QR de esta OC en un único trabajo para ahorrar material"
            onClick={(e) => {
              // IMPORTANTÍSIMO: si POTable está dentro de un <form>, sin type="button" esto hace submit
              // y te manda al top. Además frenamos propagación.
              e.preventDefault();
              e.stopPropagation();

              // Guardamos scroll actual para evitar saltos por re-render
              const y = window.scrollY || 0;

              // Abrimos ventana en el gesto del usuario (evita bloqueos de popup/print)
              const win = window.open("", "_blank");

              Promise.resolve(printAllQrsForPO(po, win))
                .catch((err) =>
                  console.error("[POTable] printAllQrsForPO error:", err)
                )
                .finally(() => {
                  requestAnimationFrame(() => {
                    window.scrollTo({ top: y, left: 0, behavior: "auto" });
                  });
                });
            }}
          >
            {isPrintingThis ? "Imprimiendo QRs…" : "🖨️ Imprimir QRs"}
          </button>

          {isScanning && !isBlocked && (
            <>
              <input
                value={scannerValue}
                onChange={(e) => setScannerValue(e.target.value)}
                onKeyDown={onScannerKeyDown}
                className="ml-auto px-2 py-1 rounded bg-white/10 border border-white/20"
                placeholder="Escaneá o escribí el código y Enter…"
                autoFocus
              />
              <button
                type="button"
                className="px-2 py-1.5 rounded-md border border-white/20 hover:bg-white/10"
                onClick={onStopScanning}
              >
                ✕ Detener
              </button>
            </>
          )}

          {/* Quitado: botón “Actualizar” (no estaba aportando valor real) */}
        </div>

        {isOpen && (
          <div className="border-t border-white/10">
            {(poInstruments?.[po.id] || []).length === 0 ? (
              <div className="px-3 py-3 text-sm opacity-70">
                Sin instrumentos aún.
              </div>
            ) : (
              <>
              <div className="flex flex-wrap items-center gap-2 border-b border-white/10 bg-sky-950/25 px-3 py-2">
                <span className="text-xs font-medium text-sky-100">
                  Cambiar todos los estados:
                </span>
                <select
                  className="min-w-[210px] rounded-md border border-sky-400/30 bg-[#141922] px-2 py-1.5 text-xs text-white"
                  value={bulkStatusByPo[po.id] || ""}
                  disabled={bulkSavingPoId === po.id}
                  onChange={(e) =>
                    setBulkStatusByPo((current) => ({
                      ...current,
                      [po.id]: e.target.value,
                    }))
                  }
                  title="Elegir el nuevo estado para todos los instrumentos de esta orden"
                >
                  <option value="">Seleccionar estado…</option>
                  {INSTRUMENT_STATUS_GROUPS.map((grp) => {
                    const allowedOptions = grp.options.filter(
                      (opt) =>
                        !HIDDEN_STATUS_OPTIONS.includes(opt.value) &&
                        canSetStatus(role, opt.value)
                    );
                    if (!allowedOptions.length) return null;
                    return (
                      <optgroup key={grp.group} label={grp.group}>
                        {allowedOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                <button
                  type="button"
                  className="rounded-md border border-sky-400/50 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-100 hover:bg-sky-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={
                    !bulkStatusByPo[po.id] ||
                    bulkSavingPoId === po.id ||
                    typeof onUpdateAllInstrumentStatuses !== "function"
                  }
                  onClick={() => handleBulkStatusUpdate(po)}
                >
                  {bulkSavingPoId === po.id
                    ? "Actualizando…"
                    : `Aplicar a los ${(poInstruments?.[po.id] || []).length}`}
                </button>
                <span className="text-[11px] text-slate-400">
                  Solo se modificarán los que tengan un estado diferente.
                </span>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full min-w-[1120px] text-sm">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-3 py-2 text-left">SERV Nro.</th>
                    <th className="px-3 py-2 text-left">ID Instrumento</th>
                    <th className="px-2 py-2 text-center whitespace-nowrap" title="Fecha de recepcion">
                      F. Recep.
                    </th>
                    <th className="px-2 py-2 text-center" title="Instrumento calibrado in situ">
                      Origen
                    </th>
                    <th className="px-3 py-2 text-left">Tag</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Descripción</th>
                    <th className="px-3 py-2 text-left">Serie</th>
                    <th className="px-2 py-2 text-center whitespace-nowrap" title="Fecha de bajada al laboratorio">
                      Lab
                    </th>
                    <th className="px-3 py-2 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(poInstruments?.[po.id] || []).map((r) => {
                    const instrumentStatus = r.instrument_status || "active";
                    const statusLabel =
                      INSTRUMENT_STATUS_LABELS[instrumentStatus] ||
                      instrumentStatus ||
                      "Sin definir";
                    const statusClass =
                      {
                        pending_reception:             "bg-gray-500/10 border border-gray-500/40 text-gray-300",
                        active:                        "bg-slate-500/10 border border-slate-500/40 text-slate-200",
                        in_progress:                   "bg-amber-500/10 border border-amber-500/40 text-amber-200",
                        paused:                        "bg-yellow-500/10 border border-yellow-500/40 text-yellow-200",
                        calibrated:                    "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200",
                        rejected:                      "bg-rose-500/10 border border-rose-500/40 text-rose-200",
                        with_certificate:              "bg-cyan-500/10 border border-cyan-500/40 text-cyan-200",
                        without_certificate:           "bg-zinc-500/10 border border-zinc-500/40 text-zinc-200",
                        delivered_without_certificate: "bg-orange-500/10 border border-orange-500/40 text-orange-200",
                        delivered_with_certificate:    "bg-indigo-500/10 border border-indigo-500/40 text-indigo-200",
                        delivered_not_calibrated:      "bg-orange-500/10 border border-orange-500/40 text-orange-200",
                        delivered_not_repaired:        "bg-orange-500/10 border border-orange-500/40 text-orange-200",
                        delivered_repaired:            "bg-indigo-500/10 border border-indigo-500/40 text-indigo-200",
                        cancelled:                     "bg-zinc-600/10 border border-zinc-600/40 text-zinc-300",
                        reviewed:                      "bg-violet-500/10 border border-violet-500/40 text-violet-200",
                        ready_to_remit:                "bg-sky-500/10 border border-sky-500/40 text-sky-200",
                        remitted:                      "bg-teal-500/10 border border-teal-500/40 text-teal-200",
                        delivered:                     "bg-indigo-500/10 border border-indigo-500/40 text-indigo-200",
                      }[instrumentStatus] ||
                      "bg-white/10 border border-white/30 text-white";

                    return (
                      <tr
                        key={`${po.id}-${r.instrument_id}`}
                        data-instrument-id={r.instrument_id}
                        data-po-id={po.id}
                        className="border-t border-white/10"
                      >
                        <td className="px-3 py-2">{r.code}</td>
                        <td className="px-3 py-2">
                          {r.instrument_lab_id || r.instrument_id || "-"}
                        </td>
                        <td
                          className="px-2 py-2 text-center whitespace-nowrap text-[11px] text-slate-300"
                          title={r.reception_date ? formatDateEU(r.reception_date) : ""}
                        >
                          {formatDateShort(r.reception_date) || "-"}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {String(r.calibration_place || "").trim().toLowerCase() === "in situ" ? (
                            <CalibrationPlaceIcon
                              place={r.calibration_place}
                              titleText="Calibrado in situ"
                            />
                          ) : (
                            <span className="text-xs text-slate-500" aria-hidden="true">
                              -
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">{r.instrument_tag || "—"}</td>
                        <td className="px-3 py-2">
                          {getInstrumentCategoryLabel(r)}
                        </td>
                        <td className="px-3 py-2">
                          {r.instrument_brand || ""} {r.instrument_model || ""}
                        </td>
                        <td className="px-3 py-2">
                          {r.instrument_serial || "—"}
                        </td>
                        <td className="px-2 py-2 text-center text-[11px] text-slate-300 whitespace-nowrap"
                            title={r.lab_entry_date ? `Bajó al lab: ${formatDateEU(r.lab_entry_date)}` : "Sin fecha de bajada"}>
                          {r.lab_entry_date ? formatDateShort(r.lab_entry_date) : (
                            <span className="text-slate-600">—</span>
                          )}
                          {r.is_broken && (
                            <span className="ml-1 text-red-400" title="Roto">🔧</span>
                          )}
                          {r.rejection_reason && (
                            <span className="ml-1 text-rose-400" title={`Rechazado: ${r.rejection_reason}`}>✕</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-[11px] ${statusClass}`}>
                              {statusLabel}
                            </span>
                            <select
                              className="min-w-[180px] rounded-md border border-white/20 bg-white/5 px-2 py-1 text-xs text-white"
                              value={instrumentStatus}
                              disabled={statusSavingId === r.instrument_id}
                              onChange={async (e) => {
                                const nextStatus = e.target.value;
                                if (nextStatus === instrumentStatus) return;
                                // Matriz de permisos: solo el rol correcto puede poner el estado.
                                if (!canSetStatus(role, nextStatus)) {
                                  e.target.value = instrumentStatus;
                                  alert("Tu rol no puede poner ese estado. Corresponde a otra persona del circuito (técnico, jefe de laboratorio u oficina).");
                                  return;
                                }
                                setStatusSavingId(r.instrument_id);
                                await Promise.resolve(
                                  onUpdateInstrumentStatus?.(r, po, nextStatus)
                                );
                                setStatusSavingId(null);
                              }}
                              title="Cambiar estado manual del instrumento"
                            >
                              {INSTRUMENT_STATUS_GROUPS.map((grp) => (
                                <optgroup key={grp.group} label={grp.group}>
                                  {grp.options
                                    .filter((opt) => !HIDDEN_STATUS_OPTIONS.includes(opt.value) || opt.value === instrumentStatus)
                                    .map((opt) => {
                                    // El estado actual siempre se muestra (aunque tu rol no
                                    // pueda ponerlo), para que el <select> lo refleje bien.
                                    const isCurrent = opt.value === instrumentStatus;
                                    const allowed = canSetStatus(role, opt.value);
                                    return (
                                      <option
                                        key={opt.value}
                                        value={opt.value}
                                        disabled={!allowed && !isCurrent}
                                      >
                                        {opt.label}
                                        {!allowed && !isCurrent ? " — (otro rol)" : ""}
                                      </option>
                                    );
                                  })}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td className="px-3 py-2 flex gap-2">
                          <button
                            type="button"
                            className="px-2 py-1 rounded-md border border-emerald-400/50 text-emerald-200 hover:bg-emerald-500/10"
                            onClick={() => onEditInstrument?.(r, po)}
                          >
                            🔧 Editar
                          </button>
                          {canDeleteInstruments && !isBlocked && (
                            <button
                              type="button"
                              className="px-2 py-1 rounded-md border border-red-500/60 text-red-200 hover:bg-red-500/10"
                              onClick={() => onDeleteInstrument?.(r, po)}
                            >
                              🗑️ Eliminar
                            </button>
                          )}
                          <button
                            type="button"
                            className="px-2 py-1 rounded-md border border-white/20 hover:bg-white/10"
                            onClick={() =>
                              onOpenQRModal?.(r, {
                                clientName: po.client_name,
                                poCode: po.code,
                              })
                            }
                          >
                            🎟️ QR
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              </>
            )}

            {showPendingForClient && (
              <div className="px-3 py-2 text-xs bg-amber-900/10 border-t border-amber-700/40 text-amber-100">
                Este cliente tiene <b>{pendingForClient}</b> instrumento
                {pendingForClient !== 1 && "s"} en la tabla de ingresos sin
                asignar a una Orden de Servicio. Usá el botón{" "}
                <b>"🔗 Vincular ingreso"</b> para traerlos a esta OC.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Solo mostramos el "Cargando…" bloqueante en la carga INICIAL (aún sin datos).
  // En los refrescos posteriores (p.ej. al cambiar el estado de un instrumento, que
  // dispara un auto-refresh por realtime) mantenemos la lista visible para que la
  // página NO colapse ni salte el scroll al tope. Así, con 50 instrumentos, el
  // administrativo se queda parado donde estaba.
  if (loading && !filteredPOs?.length)
    return (
      <div className="rounded-lg border border-white/10 p-4">Cargando…</div>
    );
  if (!loading && !filteredPOs?.length)
    return (
      <div className="rounded-lg border border-white/10 p-4">Sin resultados</div>
    );

  return (
    <div>
      {filteredPOs.map((po) => (
        <Row key={po.id} po={po} />
      ))}

      {/* 1.4) Blindar el onClose del modal */}
      <ServiceOrderObservationsModal
        open={obsModal.open}
        serviceOrderId={obsModal.serviceOrderId}
        onClose={() => {
          // Guardar la posición actual (incluso si está bloqueado)
          const y = getScrollYLockedAware();
          lastScrollYRef.current = y;

          // PASO 1: Cerrar el modal
          setObsModal({ open: false, serviceOrderId: null });

          // PASO 2: Desbloquear scroll + restaurar
          setTimeout(() => {
            scrollManager.unlock();

            // Restauración extra por si hubo re-render grande
            restoreScroll(lastScrollYRef.current);
            setTimeout(() => restoreScroll(lastScrollYRef.current), 80);
          }, 10);
        }}
      />
    </div>
  );
}
